import * as Sentry from "@sentry/browser";
import config from "config";
import dayjs from "dayjs";

import { alertActions } from "../actions";
import client from "../helpers/client";
import {
  markMaintenanceHttp503Observed,
  MAINTENANCE_PROBE_TIMEOUT_MS,
  withProbeTimeout,
} from "../helpers/maintenanceProbe";
import {
  normaliseApiPath,
  redactSensitiveValues,
  reportApiFailureToSentry,
  shouldReportApiFailure,
  summariseApiErrorResponse,
} from "../helpers/sentryApiError";
import { store } from "../store";
import {
  applyExchangeActionFilter,
  setExchangeActionStatusFlags,
} from "../views/Exchanges/utils/exchangeActionStatusFlags";
import {
  combineSqlOnlyODataFilterClauses,
  getSqlOnlyActionODataFilter,
  resolveActionFilterCompanyId,
} from "../views/Exchanges/utils/sqlOnlyActionFilterUtils";
import { getSqlOnlyStatusODataFilter } from "../views/Exchanges/utils/sqlOnlyFilterUtils";
import {
  mapSqlOnlyOrders,
  mapSqlOnlySort,
} from "../views/Exchanges/utils/sqlOnlyOrderMapper";
import featureData from "../views/featuresData.json";
import { helpersFunctions } from "../views/helpers";
import { clampSqlOnlyODataTop } from "./sqlOnlyODataConstants";
const dataObj = featureData;
/** OData entity for Exchanges list (DE-9312). */
const PBH_ORDERS_SQL_ONLY_ENTITY = "PBHOrdersSQLOnly";
export const apiService = {
  orderQueryODataSqlOnly,
  getPBHOrdersSqlOnly,
  getADSRepairs,
  repairQueryOData,
  getAircraftRegistrationList,
  getCompanySitesShipTo,
  getPriceLineRequests,
  getPriceLineRequestsQuery,
  getUploadedImageUrl,
  postAction,
  getNonShippedPbhOrders,
  getADSStatuses,
  getPBHPriorities,
  getVendorWithPreferredPrices,
  getCountryCodes,
  getRemovalTypes,
  postNewRequest,
  postNewRequestStagger,
  putAction,
  StatusCodeTest,
  getRemoteInventory,
  partStartWith,
  partStartWithOnly,
  companyStartWith,
  companyId,
  getContractList,
  getCompanySettingsByType,
  getQuotesData,
  QuotesOData,
  getQuotesPriorities,
  postOffCore,
  putOffCore,
  putOffCoreValidated,
  getOffCoreValidated,
  getInvoicesList,
  getInvoicesFilter,
  getInvoiceImage,
  getInvoiceGroupByAging,
  PostInvoices,
  getUsers,
  postPermission,
  putPermissions,
  putTeam,
  getPermissions,
  getTeams,
  getIdentity,
  createTeam,
  putUserCompany,
  GenerateInvoicePDF,
  getInvoicePDFStatus,
  getCurrencies,
  getInvoicesListImage,
  getInvoicesListPDFStatus,
  getCompanies,
  postVendorInvoices,
  postInvite,
  getInvitees,
  generateInviteCode,
  getReceiverRequests,
  getImage,
  getImageQuotes,
  checkImageExists,
  PowerBISettings,
  PowerBIGetReports,
  PowerBIGetReportsPermission,
  deletePermission,
  deleteTeamUser,
  getShippingPriority,
  putRetry,
  getSaleOrderDetailsInChat,
  checkUserPermissionCompanyIds,
  getOrderAndActions,
  getPickslips,
  putPriceLine,
  getKPIData,
  getImageBySODAutoKey,
  getPartCreatorParts,
  getPartCreatorPartsNoQuery,
  getPartCreatorPartsQuery,
  getFlightHours,
  getRecentActions,
  getPricelineBatches,
  getPriceLineAllSignOffable,
  getCustomerMappings,
  deleteUserDetails,
  getGlobalSearchData,
  checkHealth,
  checkHealthProbe,
  getReturnAirwayBill,
  getMsnAircraft,
  getRegions,
  getAircraft,
  cmpAutoKeyToCmpId,
  getWizardBatch,
  getWizardLink,
  getWizardResult,
  getWizardBatchTypes,
  getWizardBatchResultFilt,
  getGlobalTeams,
  getTableFields,
  getCompanyContacts,
  getCompanyContactIds,
  getAllPermissions,
  getCompanyContactByCompany,
  getOperatorCompany,
  checkMaintenance,
  resetMaintenanceCheckInFlight,
  deleteItem,
  getCompanyContracts,
  getAircraftByCompany,
  applyActionFilterToRows,
  processSqlOnlyOrderRows,
};
//
function deleteItem(queryString) {
  return new Promise((resolve, reject) => {
    client
      .delete(`${queryString}`)
      .then((response) => {
        let stringifiedResult = JSON.stringify(response);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        if (stringifiedResult && sentryLog.includes("embedToken")) {
          sentryLog =
            sentryLog.split("embedToken")[0] +
            sentryLog.split("reportPermissions")[
              sentryLog.split("reportPermissions").length - 1
            ];
        }
        console.info("result BI:", sentryLog);
        return resolve(response);
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => console.error(err));
}

function cmpAutoKeyToCmpId(cmpAutoKey) {
  let queryString = `${config.ADSAPI}/api/Companies/byCmpAutoKey?cmpAutoKey=${cmpAutoKey}`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve(result.data);
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

/** Anonymous GET for maintenance gate (axios error shape, not stringified). */
function checkHealthProbe() {
  const queryString = `${config.ADSAPI}/api/Health`;
  return client.get(queryString).then((result) => result.status);
}

function checkHealth() {
  let queryString = `${config.ADSAPI}/api/Health`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result BI:", sentryLog);
        return resolve(result);
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

// Share one in-flight Maintenance probe so Login mount + auth-button clicks
// await the same result instead of rejecting duplicates (DE-9405).
let maintenanceCheckPromise = null;

/** Drop shared in-flight probe so the next check issues a new GET (pre-auth re-check). */
function resetMaintenanceCheckInFlight() {
  maintenanceCheckPromise = null;
}

function checkMaintenance() {
  if (maintenanceCheckPromise) {
    return maintenanceCheckPromise;
  }

  const queryString = `${config.API}/api/Maintenance`;

  const probe = withProbeTimeout(
    client.get(`${queryString}`, {
      // Always surface HTTP status on the response object (503 maintenance page).
      validateStatus: () => true,
    }),
    MAINTENANCE_PROBE_TIMEOUT_MS,
  )
    .then((result) => {
      if (result.status === 200) {
        return result.status;
      }
      if (result.status === 503) {
        markMaintenanceHttp503Observed("checkMaintenance.response", {
          status: result.status,
        });
      }
      const err = new Error(
        `Maintenance probe HTTP ${result.status}`,
      );
      err.response = result;
      err.isAxiosError = true;
      throw err;
    })
    .catch((err) => {
      if (err?.response?.status === 503) {
        markMaintenanceHttp503Observed("checkMaintenance.catch", {
          status: err.response.status,
        });
      }
      console.error("Maintenance probe failed:", err?.message || err);
      if (err.response) {
        console.error("Maintenance probe status:", err.response.status);
      }
      throw err;
    })
    .finally(() => {
      if (maintenanceCheckPromise === probe) {
        maintenanceCheckPromise = null;
      }
    });

  maintenanceCheckPromise = probe;
  return probe;
}

function getMsnAircraft(searchBy, queryStr) {
  let t0 = performance.now();
  let t1;

  let queryString =
    queryStr && searchBy == "MSNOrAR"
      ? `${config.API}/odata/AircraftRegistry?$filter=MSN eq '${queryStr}' or  CN eq '${queryStr}' or Registration eq '${queryStr}'`
      : queryStr
        ? `${config.API}/odata/AircraftRegistry?$filter=${searchBy} eq '${queryStr}'`
        : `${config.API}/odata/AircraftRegistry`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getQuotesData " + (t1 - t0) + " milliseconds.");
        // hack way to get "@odata.count"
        let count;
        if (result && result.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        }
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getRegions(country, options) {
  let t0 = performance.now();
  let t1;
  //on opening the form we get OperatorCountry : "United States of America (the)", which works fine (from /odata/AircraftRegistry) (we just take off the 'the' to match capa country regions call)
  //but we sometimes get mismatches from /compnaiesView, eg. it will return USA, so for this we're instead using country code
  let queryString =
    options && options.countryCode
      ? `${config.API}/odata/CAPACountryRegions?$filter=CountryCode eq '${country}'`
      : `${config.API}/odata/CAPACountryRegions?$filter=OperatorCountry eq '${country}'`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getQuotesData " + (t1 - t0) + " milliseconds.");
        // hack way to get "@odata.count"

        if (result && result.data) {
          return resolve({ data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getAircraft(rows = 30, skip = 0, searchBy, queryStr) {
  let t0 = performance.now();
  let t1;

  let queryString = queryStr
    ? `${config.API}/odata/CreateAircraftRequest?$filter=contains(toupper(${searchBy}), toupper('${queryStr}')) eq true&$top=${rows}&$skip=${skip}&$count=true&$orderby=Id desc`
    : `${config.API}/odata/CreateAircraftRequest?$top=${rows}&$skip=${skip}&$count=true&$orderby=Id desc`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getQuotesData " + (t1 - t0) + " milliseconds.");
        // hack way to get "@odata.count"
        let count;
        if (result && result.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getReceiverRequests(rows = 30, skip = 0, searchBy, queryStr) {
  let t0 = performance.now();
  let t1;

  let queryString = queryStr
    ? `${config.API}/odata/ReceiverRequest?$filter=contains(toupper(${searchBy}), toupper('${queryStr}')) eq true&$orderby=DateCreated desc&$top=${rows}&$skip=${skip}&$count=true`
    : `${config.API}/odata/ReceiverRequest?$orderby=DateCreated desc&$top=${rows}&$skip=${skip}&$count=true`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getQuotesData " + (t1 - t0) + " milliseconds.");
        // hack way to get "@odata.count"
        let count;
        if (result && result.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getImage(sourcePk, table, imc) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ADSAPI}/api/Images/SourceTableAndPkAndImc?sourceTable=${table}&sourcePk=${sourcePk}&imcAutoKey=${imc}`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log(
          "getVendorInvoicesListImage " + (t1 - t0) + " milliseconds.",
        );
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getImageBySODAutoKey(sod) {
  let queryString = `${config.API}/api/Images/TearDown/SaleOrderDetail/${sod}`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getImageQuotes(sourcePk) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ADSAPI}/api/Images/SourceTableAndPk?sourceTable=STOCK&sourcePk=${sourcePk}`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getImageQuotes " + (t1 - t0) + " milliseconds.");
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function checkImageExists(guid) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ADSAPI}/api/Images/Exists?Guid=${guid}`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getImageQuotes " + (t1 - t0) + " milliseconds.");
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getInvoiceImage(options) {
  //temp hardocded key : 1147344 // ${options.key}
  let t0 = performance.now();
  let t1;
  let imcNo = options.imc ? options.imc : config.invoiceimcAutoKey;
  let queryString = `${config.ADSAPI}/api/Images/SourceTableAndPkAndImc?sourceTable=INVC_HEADER&sourcePk=${options.key}&imcAutoKey=${imcNo}`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getInvoiceImage " + (t1 - t0) + " milliseconds.");
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getCompanies() {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.API}/odata/invoiceview/?$apply=groupby((CompanyAutoKey, CompanyName), aggregate($count as Total))`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getCompanies " + (t1 - t0) + " milliseconds.");
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getInvoicesListImage(options) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ADSAPI}/api/Images/SourceTableAndPkAndImc?sourceTable=companies&sourcePk=${options.key}&imcAutoKey=485`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log(
          "getInvoicesListImage " + (t1 - t0) + " milliseconds.",
        );
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getInvoicePDFStatus(options) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ADSAPI}/api/TaskRequests/byTableKey?tableName=INVC_HEADER&key=${options.key}`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getInvoicePDFStatus " + (t1 - t0) + " milliseconds.");
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}
function getInvoicesListPDFStatus(options) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ADSAPI}/api/TaskRequests/byTableKey?tableName=companies&key=${options.key}`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log(
          "getInvoicesListPDFStatus " + (t1 - t0) + " milliseconds.",
        );
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function GenerateInvoicePDF(options) {
  return new Promise((resolve, reject) => {
    return client
      .post(`${config.ADSAPI}/api/TaskRequests`, options)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        console.error("payload: ", JSON.stringify(options));
        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("Payload:", JSON.stringify(options));
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException("Generate Invoice Error");
        }
        if (error.response != undefined) {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
        } else {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
        }

        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function PostInvoices(options) {
  return new Promise((resolve, reject) => {
    return client
      .post(`${config.API}/api/InvoiceRemittance/`, options)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        console.error("user input: ", JSON.stringify(options));
        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("User input:", JSON.stringify(options));
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException("Post Invoice error");
        }
        if (error.response != undefined) {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
        } else {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
        }
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getCurrencies() {
  let t0 = performance.now();
  let t1;
  let query = `${config.API}/odata/invoiceview?$apply=groupby((CurrencyCode),%20aggregate($count%20as%20Total))`;
  return new Promise((resolve, reject) => {
    client
      .get(`${query}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getCurrencies " + (t1 - t0) + " milliseconds.");
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function QueryODataFromUrl(options) {
  //let's read the params to find in any is usable for a query
  let possibleParams = [];
  let columnsData = options.columnsDataTable;
  Object.keys(columnsData).forEach((rows) => {
    columnsData[rows].forEach((row) => {
      possibleParams.push(row.data);
    });
  });
  var filteredAray = possibleParams.filter(function (e) {
    return e !== "Actions";
  });
  //if the params can be used then we can make the QueryOdata Call
  if (filteredAray.includes(Object.keys(options.params)[0])) {
    return options.QueryOData({
      property: Object.keys(options.params)[0],
      query: options.params[Object.keys(options.params)[0]],
      rows: 10,
      skip: 0,
      action: null,
    });
  } else {
    return false;
  }
}

function CanQueryFromURL(options) {
  // Takes column metadata from the dataview, or a `canQuery`-shaped object for fields that are URL-filterable but not shown in the grid.
  let possibleParams = [];
  let columnsData = options.columnsDataTable;
  Object.keys(columnsData).forEach((rows) => {
    columnsData[rows].forEach((row) => {
      possibleParams.push(row.data);
    });
  });
  var filteredAray = possibleParams.filter(function (e) {
    return e !== "Actions";
  });
  //if the params can be used then we can make the QueryOdata Call
  if (filteredAray.includes(Object.keys(options.params)[0])) {
    return true;
  } else {
    return false;
  }
}

// -----------------------------------------------------------------------------
// PBHOrdersSQLOnly row processing (DE-9312)
// -----------------------------------------------------------------------------
// After each SQL-only list fetch: map Raw* fields, then set action/waiting flags
// for the "Actions" toolbar filter (`applyActionFilterToRows`).

/** Action/waiting flags for SQL-only rows — status strings match `sqlOnlyOrderMapper`. */
function setActionStatusSqlOnly(data, companyId) {
  setExchangeActionStatusFlags(data, companyId);
}

/** Map raw SQL-only OData rows to exchange list shape + action flags. */
function processSqlOnlyOrderRows(rows, action, companyId) {
  const mapped = mapSqlOnlyOrders(rows || []);
  const resolvedCompanyId = resolveActionFilterCompanyId(
    action?.user,
    action?.user?.companyId ?? companyId,
  );
  setActionStatusSqlOnly(mapped, resolvedCompanyId);
  StatusNameFormat(mapped);
  return mapped;
}

function applyActionFilterToRows(rows, action) {
  return applyExchangeActionFilter(rows, action);
}

// -----------------------------------------------------------------------------
// PBHOrdersSQLOnly OData queries (DE-9312) — used by Exchanges list/export only
// -----------------------------------------------------------------------------
// `orderQueryODataSqlOnly`: filtered/sorted list (search, status, action, pagination)
// `getPBHOrdersSqlOnly`: default list; delegates to `orderQueryODataSqlOnly` when action filter set
// Status filter dropdown options are static (`exchangesStatusFilters.ts`); no groupby fetch.

/** Turn a toolbar status label into an OData `$filter` clause for SQL-only list queries. */
function buildSqlOnlyStatusClause(displayStatus) {
  const filter = getSqlOnlyStatusODataFilter(displayStatus);
  return filter ? filter.clause : null;
}

/** Turn an action toolbar value into an OData `$filter` clause for SQL-only list queries. */
function buildSqlOnlyActionClause(action) {
  if (!action || !action.name) {
    return null;
  }
  const filter = getSqlOnlyActionODataFilter(action.name, action.user);
  return filter ? filter.clause : null;
}

//Function to map status description coming from api call and change them for more readable version
//todo: can refactor this after adam pushes
function StatusNameFormat(data) {
  data.forEach((item) => {
    if (item.OutboundStatus) {
      switch (item.OutboundStatus) {
        case "ExchangeRequest":
          item.OutboundStatusView = "Request Received";
          break;
        case "Instock":
          item.OutboundStatusView = "In-stock";
          break;
        case "Alternative":
          item.OutboundStatusView = "Alternate Offered";
          break;
        case "AcceptDecline":
          item.OutboundStatusView = "Accept/Decline";
          break;
        default:
          item.OutboundStatusView = item.OutboundStatus;
          break;
      }
    }
    if (item.InboundStatus) {
      switch (item.InboundStatus) {
        case "AwaitingOffCoreInformation":
          item.InboundStatusView = "Awaiting OffCore Info";
          break;
        case "OffcorePart":
          item.InboundStatusView = "OffCore Part Info";
          break;
        case "OffcoreShipping":
          item.InboundStatusView = "OffCore Shipping";
          break;
        default:
          item.InboundStatusView = item.InboundStatus;
          break;
      }
    }
  });
}
function formatName(data) {
  if (data) {
    switch (data) {
      case "ExchangeRequest":
        return "Request Received";
        break;
      case "Instock":
        return "In-stock";
        break;
      case "Alternative":
        return "Alternate Offered";
        break;
      case "AcceptDecline":
        return "Accept/Decline";
        break;
      case "AwaitingOffCoreInformation":
        return "Awaiting OffCore Info";
        break;
      case "OffcorePart":
        return "OffCore Part Info";
        break;
      case "OffcoreShipping":
        return "OffCore Shipping";
        break;
      default:
        return data;
        break;
    }
  }
}

function getCountryCodes() {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.API}/api/CountryCodes`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getCountryCodes " + (t1 - t0) + " milliseconds.");
        let arr;
        if (result?.data) {
          arr = result.data.map((obj) => {
            return obj;
          });
        } else {
          console.error("Unexpected result:", result);
        }

        return resolve(arr);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getRemovalTypes() {
  let t0 = performance.now();
  let t1;
  const queryString = `${config.ADSAPI}/api/RemovalTypes`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getRemovalTypes " + (t1 - t0) + " milliseconds.");

        const items = Array.isArray(result?.data) ? result.data : [];
        const mapped = items.map((raw) => {
          const value = raw?.name;
          const code = raw?.name;
          const id = raw?.id;
          return { code, value, id };
        });
        return resolve(mapped);
      })
      .catch((error) => {
        console.error("error: ", error);
        const errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getContractList(companyId) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ADSAPI}/api/Companies/withContract?Id=${companyId}`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getContractList " + (t1 - t0) + " milliseconds.");
        let contracts = [];

        if (result && result.data) {
          contracts = result.data.companyContracts.map((item) => {
            return item;
          });

          return resolve(contracts);
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getCompanySettingsByType(companyId, settingTypeId) {
  const queryString = `${config.ADSAPI}/api/CompanySettings/byCompanyIdAndType/${companyId}/${settingTypeId}`;
  return client.get(queryString).then((result) => {
    return Array.isArray(result?.data) ? result.data : [];
  });
}

function getOperatorCompany(companyId) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ADSAPI}/api/CompaniesView/startsWith?companyName=${companyId}&limit=1000`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getInvoicesList " + (t1 - t0) + " milliseconds.");
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getInvoicesList() {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.API}/odata/invoiceview?&$orderby=EntryDate`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getInvoicesList " + (t1 - t0) + " milliseconds.");
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}
function getInvoicesFilter(options) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.API}/odata/invoiceview?$filter=contains(toupper(Aging), toupper('${options.property}')) eq true&$orderby=EntryDate`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getInvoicesFilter " + (t1 - t0) + " milliseconds.");
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getShippingPriority(companyId, mappingType) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ADSAPI}/api/CustomerMappings/byCompanyIdAndType/${companyId}/${mappingType}/sortBySequence`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getShippingPriority " + (t1 - t0) + " milliseconds.");
        //let shipping = result.data.filter((item) => item.type === 1); //filter type 1 = ShippingPriority
        if (result && result.data) {
          return resolve(result.data);
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getCustomerMappings(companyId) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ADSAPI}/api/CustomerMappings/byCompanyId/${companyId}`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getShippingPriority " + (t1 - t0) + " milliseconds.");
        //let shipping = result.data.filter((item) => item.type === 1); //filter type 1 = ShippingPriority
        if (result && result.data) {
          return resolve(result.data);
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getCompanyContracts(companyId) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ADSAPI}/api/Contracts/byCompanyId/${companyId}`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getShippingPriority " + (t1 - t0) + " milliseconds.");
        //let shipping = result.data.filter((item) => item.type === 1); //filter type 1 = ShippingPriority
        if (result && result.data) {
          return resolve(result.data);
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getInvoiceGroupByAging() {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.API}/odata/invoiceview?$apply=groupby((Aging), aggregate($count as Total,InvoiceAmount with sum as TotalAmount))&$orderby=Aging`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log(
          "getInvoiceGroupByAging " + (t1 - t0) + " milliseconds.",
        );
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getRemoteInventory() {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.API}/api/RemoteInventory/Legacy`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getRemoteInventory " + (t1 - t0) + " milliseconds.");
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function partStartWith(partNumber) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ADSAPI}/api/Parts/startsWith?partNumber=${partNumber}&limit=1000`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("partStartWith " + (t1 - t0) + " milliseconds.");
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function partStartWithOnly(partNumber) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ADSAPI}/api/PartsOnly/startsWith?partNumber=${partNumber}&limit=1000`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("partStartWithOnly " + (t1 - t0) + " milliseconds.");
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}
function companyStartWith(name) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ADSAPI}/api/Companies/startsWith?companyName=${name}&limit=1000`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("companyStartWith " + (t1 - t0) + " milliseconds.");
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function companyId(id) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ADSAPI}/api/Companies/${id}`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("companyStartWith " + (t1 - t0) + " milliseconds.");
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

const dedupeCustomerPriorityGroupbyRows = (rawData) => {
  if (!rawData || !Array.isArray(rawData)) {
    return null;
  }
  const seen = new Set();
  const priorities = [];
  rawData.forEach((obj) => {
    const p = obj.CustomerPriority;
    if (p != null && p !== "" && !seen.has(String(p).toLowerCase())) {
      seen.add(String(p).toLowerCase());
      priorities.push(p);
    }
  });
  return priorities;
};

function getPBHPriorities() {
  let t0 = performance.now();
  let t1;
  const queryString = `${config.ActionAPI}/odata/${PBH_ORDERS_SQL_ONLY_ENTITY}?$apply=groupby((CustomerPriority))`;
  return new Promise((resolve, reject) => {
    client
      .get(queryString)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getPBHPriorities " + (t1 - t0) + " milliseconds.");
        const rawData = result && (result.data?.value ?? result.data);
        const priorities = dedupeCustomerPriorityGroupbyRows(rawData);
        if (priorities) {
          return resolve(priorities);
        }
        store.dispatch(
          alertActions.error(
            `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
          ),
        );
        return resolve([]);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

// fix 08/01/2020 function was missing from the api and called from UpdateRepairRequestAction
function getVendorWithPreferredPrices(partNumber) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ActionAPI}/api/Helper/PreferredVendor?partNumber=${partNumber}`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log(
          "getVendorWithPreferredPrices " + (t1 - t0) + " milliseconds.",
        );
        let count;
        if (result && result.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}



function orderQueryODataSqlOnly(
  options,
  sort = { sortby: "creationDate", sortorder: "desc" },
  entireDataSet,
) {
  // Exchanges filtered list: builds `$filter` on Raw* fields (not InboundStatus/OutboundStatus).
  // `options.property` InboundStatus|OutboundStatus is a legacy shape — we only use
  // the label string and resolve it via `buildSqlOnlyStatusClause`.
  // `inOrOutStatusFilterLabel` is the display label when status is combined with text search.
  if (
    options?.query?.toString().includes("#") ||
    options?.inOrOutStatusFilterLabel?.toString().includes("#")
  ) {
    store.dispatch(
      alertActions.error(
        `Please remove the # from the search term. If you have not searched, this could have been set in the URL.`,
      ),
    );
  }
  let t0 = performance.now();
  let t1;
  const intProperties = ["ExchangeRequestId", "CompanyAutoKey"];
  const propEqQryWithString = ["Priority"];
  const isStatusProperty =
    options.property === "InboundStatus" ||
    options.property === "OutboundStatus";
  const statusLabel = isStatusProperty
    ? options.query
    : options.inOrOutStatusFilterLabel;
  const statusClause = buildSqlOnlyStatusClause(statusLabel);
  const eqWithString = propEqQryWithString.includes(options.property);
  const isPropertyIntType = intProperties.includes(options.property);
  const hasTextSearch =
    options.property &&
    options.query &&
    !isStatusProperty &&
    options.inOrOutStatusFilterLabel === undefined;
  const hasTextWithStatus =
    options.property &&
    options.query &&
    !isStatusProperty &&
    options.inOrOutStatusFilterLabel !== undefined;

  let filterClause = null;
  if (isStatusProperty && statusClause) {
    filterClause = statusClause;
  } else if (hasTextWithStatus) {
    const combinedStatusClause = buildSqlOnlyStatusClause(
      options.inOrOutStatusFilterLabel,
    );
    const textClause = eqWithString
      ? `toupper(${options.property}) eq toupper('${options.query}')`
      : isPropertyIntType
        ? `${options.property} eq ${options.query}`
        : `contains(toupper(${options.property}), toupper('${options.query}')) eq true`;
    filterClause = combinedStatusClause
      ? `${textClause} and ${combinedStatusClause}`
      : textClause;
  } else if (hasTextSearch) {
    filterClause = eqWithString
      ? `toupper(${options.property}) eq toupper('${options.query}')`
      : isPropertyIntType
        ? `${options.property} eq ${options.query}`
        : `contains(toupper(${options.property}), toupper('${options.query}')) eq true`;
  } else if (statusClause) {
    filterClause = statusClause;
  }

  const actionClause = buildSqlOnlyActionClause(options.action);
  filterClause = combineSqlOnlyODataFilterClauses(
    filterClause,
    actionClause,
  );

  // PBHOrdersSQLOnly has no InboundStatus/OutboundStatus columns — translate
  // those sort fields to their Raw* discriminators (else OData returns 400).
  const sqlOnlySort = mapSqlOnlySort(sort);

  let queryString = `${config.ActionAPI}/odata/${PBH_ORDERS_SQL_ONLY_ENTITY}`;
  if (filterClause) {
    queryString += `?$filter=${filterClause}`;
  }
  if (!entireDataSet) {
    const includeCount = options.includeCount !== false;
    const separator = filterClause ? "&" : "?";
    queryString += `${separator}$orderby=${sqlOnlySort.sortby} ${sqlOnlySort.sortorder}&$top=${clampSqlOnlyODataTop(options.rows)}&$skip=${options.skip}`;
    if (includeCount) {
      queryString += "&$count=true";
    }
  } else {
    const separator = filterClause ? "&" : "?";
    queryString += `${separator}$orderby=${sqlOnlySort.sortby} ${sqlOnlySort.sortorder}`;
  }

  const requestConfig = options.signal ? { signal: options.signal } : undefined;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`, requestConfig)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log(
          "orderQueryODataSqlOnly " + (t1 - t0) + " milliseconds.",
        );
        if (result && result.data) {
          const rawRows = result.data.value || [];
          const processedRows = processSqlOnlyOrderRows(
            rawRows,
            options.action,
            options.action?.user?.companyId,
          );
          let filteredData;
          let count;
          if (options.action && options.action.name) {
            if (options.skipActionClientFilter) {
              filteredData = processedRows;
              count = ["", processedRows.length];
            } else {
              const actionResult = applyActionFilterToRows(
                processedRows,
                options.action,
              );
              filteredData = actionResult.data;
              count = ["", actionResult.count];
            }
          } else {
            count = Object.keys(result.data).map((e) => result.data[e]);
          }
          const finalCount =
            options.includeCount === false
              ? undefined
              : Array.isArray(count[1])
                ? count[1].length
                : count[1];
          return resolve({
            count: finalCount,
            data: filteredData ? filteredData : processedRows,
            odataRowCount: rawRows.length,
          });
        }
        store.dispatch(
          alertActions.error(
            `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
          ),
        );
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getPBHOrdersSqlOnly(
  rows,
  skip,
  sort = { sortby: "CreationDate", sortorder: "desc" },
  action,
  options,
) {
  if (action && action.name && buildSqlOnlyActionClause(action)) {
    return orderQueryODataSqlOnly(
      {
        rows,
        skip,
        action,
        includeCount: !options || options.includeCount !== false,
      },
      sort,
      false,
    );
  }

  skip = skip || 0;
  let t0 = performance.now();
  let t1;
  const includeCount = !options || options.includeCount !== false;
  // Translate InboundStatus/OutboundStatus → Raw* (no such columns on SQL-only).
  const sqlOnlySort = mapSqlOnlySort(sort);
  let queryString = `${config.ActionAPI}/odata/${PBH_ORDERS_SQL_ONLY_ENTITY}?$orderby=${sqlOnlySort.sortby} ${sqlOnlySort.sortorder}&$top=${clampSqlOnlyODataTop(rows)}&$skip=${skip}`;
  if (includeCount) {
    queryString += "&$count=true";
  }

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getPBHOrdersSqlOnly " + (t1 - t0) + " milliseconds.");
        if (result && result.data && result.data.value) {
          const processedRows = processSqlOnlyOrderRows(
            result.data.value,
            action,
            action?.user?.companyId ??
              options?.user?.companyId ??
              options?.user?.company?.id,
          );
          let filteredData;
          if (action && action.name) {
            const actionResult = applyActionFilterToRows(
              processedRows,
              action,
            );
            filteredData = actionResult.data;
            var count = ["", actionResult.count];
          } else if (result && result.data) {
            var count = Object.keys(result.data).map((e) => result.data[e]);
          }
          if (result && result.data) {
            return resolve({
              count: includeCount ? count[1] : undefined,
              data: filteredData ? filteredData : processedRows,
            });
          }
        }
        store.dispatch(
          alertActions.error(
            `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
          ),
        );
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getQuotesData(
  rows,
  skip,
  sort = { sortby: "DateCreated", sortorder: "desc" },
) {
  skip = skip || 0;
  //let t0 = performance.now();

  let t0 = performance.now();
  let t1;
  let queryString = rows
    ? `${config.API}/odata/QuoteRequestors?$orderby=${sort.sortby} ${sort.sortorder}&$top=${rows}&$skip=${skip}&$count=true`
    : `${config.API}/odata/QuoteRequestors?$orderby=${sort.sortby} ${sort.sortorder}&$count=true`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getQuotesData " + (t1 - t0) + " milliseconds.");
        // hack way to get "@odata.count"
        let count;
        if (result?.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function QuotesOData(
  options,
  sort = { sortby: "DateCreated", sortorder: "desc" },
  entireDataSet,
) {
  let t0 = performance.now();
  let t1;
  const intProperties = ["NumberOfItems"];
  let isPropertyIntType = intProperties.includes(options.property);
  if (
    options.outboundStatus === undefined ||
    options.outboundStatus === null
  ) {
    var queryString = isPropertyIntType
      ? `${config.API}/odata/QuoteRequestors?$filter=${options.property} eq ${options.query}`
      : `${config.API}/odata/QuoteRequestors?$filter=contains(toupper(${options.property}), toupper('${options.query}')) eq true`;
  } else {
    var queryString = isPropertyIntType
      ? `${config.API}/odata/QuoteRequestors?$filter=${options.property} eq ${options.query} and status eq '${options.outboundStatus}'`
      : `${config.API}/odata/QuoteRequestors?$filter=contains(toupper(${options.property}), toupper('${options.query}')) eq true and status eq '${options.outboundStatus}'`;
  }
  if (!entireDataSet) {
    queryString += `&$orderby=${sort.sortby} ${sort.sortorder}&$top=${options.rows}&$skip=${options.skip}&$count=true`;
  } else {
    queryString += `&$orderby=${sort.sortby} ${sort.sortorder}`;
  }

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("QuotesOData " + (t1 - t0) + " milliseconds.");
        let count;
        if (result && result.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getQuotesPriorities() {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.API}/odata/QuoteRequestors?$apply=groupby((Priority))`;
  return new Promise((resolve, reject) => {
    client
      .get(queryString)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getQuotesPriorities " + (t1 - t0) + " milliseconds.");
        if (result && result.data) {
          let priorities = result.data.map((obj) => {
            return obj.Priority;
          });
          return resolve(priorities);
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getADSRepairs(
  rows,
  skip,
  sort = { sortby: "creationDate", sortorder: "desc" },
  options,
) {
  let t0 = performance.now();
  let t1;
  let queryString = rows
    ? `${config.API}/odata/repairRequests?$orderby=${sort.sortby} ${sort.sortorder}&$top=${rows}&$skip=${skip}&$count=true`
    : `${config.API}/odata/repairRequests?$orderby=${sort.sortby} ${sort.sortorder}&$count=true`;

  if (options && options.param && Object.keys(options.param).length > 0) {
    if (
      CanQueryFromURL({
        params: options.param,
        columnsDataTable: dataObj.PBHRepairs.columnsDataTable,
      }) === true
    ) {
      return repairQueryOData({
        property: Object.keys(options.param)[0],
        query: options.param[Object.keys(options.param)[0]],
        rows: 10,
        skip: 0,
        action: null,
      });
    } else if (
      CanQueryFromURL({
        params: options.param,
        columnsDataTable: dataObj.PBHRepairs.canQuery,
      }) === true
    ) {
      //get the matching object from featuresData to see if there's a type
      let match = dataObj.PBHRepairs.canQuery.row.filter((obj) => {
        return obj.data == Object.keys(options.param)[0];
      });

      //need to make sure that if there is a type of 'number' there aren't any non-number chars in the string
      if (
        (match[0].type == "number" &&
          //isNaN will work to prevent strings with numbers and characters from getting through  unless it just has a space
          //if there is a space, using Number with convert it to 0, so we use Number to check it != 0
          !isNaN(options.param[Object.keys(options.param)[0]]) &&
          Number(options.param[Object.keys(options.param)[0]]) != 0) ||
        match[0].type != "number"
      ) {
        return repairQueryOData({
          property: Object.keys(options.param)[0],
          query: options.param[Object.keys(options.param)[0]],
          rows: 10,
          skip: 0,
          action: null,
        });
      }
    }
  }

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getADSRepairs " + (t1 - t0) + " milliseconds.");
        // hack way to get "@odata.count"
        let count;
        if (result && result.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getADSStatuses() {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.API}/odata/repairRequests?$apply=groupby((status), aggregate($count as Total))`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getADSStatuses " + (t1 - t0) + " milliseconds.");
        if (result && result.data) {
          let data = result.data.map((item) => {
            return { projectStatus: item.status, Total: item.Total };
          });
          return resolve({
            status: data,
          });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function repairQueryOData(
  options,
  sort = { sortby: "creationDate", sortorder: "desc" },
  entireDataSet,
) {
  let t0 = performance.now();
  let t1;
  //checking if properties are an integer to change for the call
  const intProperties = ["RepairRequestId", "shippingPriority"];
  let isPropertyIntType = intProperties.includes(options.property);

  if (
    options.outboundStatus === undefined ||
    options.outboundStatus === null ||
    options.property === "RepairRequestId" //if it's RepairRequestId, we're filtering on one specific repair (after editing a part number) and therefore don't want to filter on status
  ) {
    var queryString = isPropertyIntType
      ? `${config.API}/odata/repairRequests?$filter=${options.property} eq ${options.query}`
      : `${config.API}/odata/repairRequests?$filter=contains(toupper(${options.property}), toupper('${options.query}')) eq true`;
  } else {
    var queryString = isPropertyIntType
      ? `${config.API}/odata/repairRequests?$filter=${options.property} eq ${options.query} and projectStatus eq '${options.outboundStatus}'`
      : `${config.API}/odata/repairRequests?$filter=contains(toupper(${options.property}), toupper('${options.query}')) eq true and status eq '${options.outboundStatus}'`;
  }
  if (!entireDataSet) {
    queryString += `&$orderby=${sort.sortby} ${sort.sortorder}&$top=${options.rows}&$skip=${options.skip}&$count=true`;
  } else {
    queryString += `&$orderby=${sort.sortby} ${sort.sortorder}`;
  }

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("repairQueryOData " + (t1 - t0) + " milliseconds.");
        let count;
        if (result && result.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getAircraftRegistrationList() {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.API}/api/CompanyAcReg`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log(
          "getAircraftRegistrationList " + (t1 - t0) + " milliseconds.",
        );
        return resolve(result);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getCompanySitesShipTo(companyAutoKey) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.API}/odata/companysitesview?$filter=companyAutoKey eq ${companyAutoKey}`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log(
          "getCompanySitesShipTo " + (t1 - t0) + " milliseconds.",
        );
        // hack way to get "@odata.count"
        let count;
        //here
        if (result && result.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getNonShippedPbhOrders() {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ActionAPI}/api/ExchangeRequest/NotShipped`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log(
          "getNonShippedPbhOrders " + (t1 - t0) + " milliseconds.",
        );
        return resolve(result);
      })
      .catch((error) => {
        if (error.response != undefined) {
          console.error(
            "error: ",
            error.response.statusText,
            error.response.status,
            error.response.data,
          );
          window.alert(`Sorry there has been an error.
          \nThe following exception has occurred: ${error.response.statusText} 
          \nError code: ${error.response.status}
          \n ${error.response.data}`);
        } else {
          window.alert("Sorry there has been an error.");
          console.error("error: ", error);
        }
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getOrderAndActions(id, orderType) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ActionAPI}/api/${orderType}/${id}/Actions`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getOrderAndActions " + (t1 - t0) + " milliseconds.");
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getUploadedImageUrl(id, orderType, fileKey) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ActionAPI}/api/${orderType}/${id}/Actions/File/${fileKey}`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getUploadedImageUrl " + (t1 - t0) + " milliseconds.");
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function postOffCore(action) {
  return new Promise((resolve, reject) => {
    return client
      .post(`${config.API}/api/Offcore`, action)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        console.error("user input: ", JSON.stringify(action));
        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("User input:", JSON.stringify(action));
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException("Post Offcore error");
        }
        if (error.response != undefined) {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
        } else {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
        }
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function putOffCore(id, action) {
  return new Promise((resolve, reject) => {
    return client
      .put(`${config.API}/api/Offcore?Id=${id}`, action)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        console.error("user input: ", JSON.stringify(action));
        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("User input:", JSON.stringify(action));
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException("Post Offcore error");
        }
        if (error.response != undefined) {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
        } else {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
        }
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function putOffCoreValidated(id, validated) {
  return new Promise((resolve, reject) => {
    return client
      .put(
        `${config.ActionAPI}/api/OffcoreDetail/${id}/Validated/${validated}`,
      )
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        console.error("user input: ", { id, validated });
        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("User input:", { id, validated });
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException("Put Offcore Validated error");
        }
        if (error.response != undefined) {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
        } else {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
        }
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getOffCoreValidated(id) {
  return new Promise((resolve, reject) => {
    return client
      .get(`${config.ActionAPI}/api/OffcoreDetail/${id}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          // Extract just the validated field
          const validated =
            typeof result.data.validated === "boolean"
              ? result.data.validated
              : false;
          return resolve({ data: { validated } });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        console.error("user input: ", { id });
        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("User input:", { id });
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException("Get Offcore Validated error");
        }
        if (error.response != undefined) {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
        } else {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
        }
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

// function postPaperwork(uploadEndpoint, payload, headers) {
//   return new Promise((resolve, reject) => {
//     return client
//       .post(uploadEndpoint, payload, headers)
//       .then((result) => {
//         let stringifiedResult = JSON.stringify(result);
//         let sentryLog = stringifiedResult;
//         if (
//           stringifiedResult &&
//           stringifiedResult.includes("Authorization")
//         ) {
//           let arrOne = stringifiedResult.split("Authorization");
//           let arrTwo = stringifiedResult.split("transformRequest");
//           sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
//         }
//         console.info("result:", sentryLog);
//         if (result && result.data) {
//           return resolve({ data: result.data });
//         } else {
//           store.dispatch(
//             alertActions.error(
//               `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`
//             )
//           );
//         }
//       })
//       .catch((error) => {
//         let errorString = JSON.stringify(error);
//         let sentryError;
//         if (errorString && errorString.includes("Authorization")) {
//           sentryError = errorString.split("Authorization")[0];
//         } else {
//           sentryError = errorString;
//         }
//         console.error("error:", sentryError);

//         console.error("user input: ", JSON.stringify(payload));
//         if (
//           error &&
//           error.response &&
//           error.response.status &&
//           error.response.status != 401
//         ) {
//           let stringifiedResult = JSON.stringify(error.response);
//           let sentryLog = stringifiedResult;
//           if (
//             stringifiedResult &&
//             stringifiedResult.includes("Authorization")
//           ) {
//             let arrOne = stringifiedResult.split("Authorization");
//             let arrTwo = stringifiedResult.split("transformRequest");
//             sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
//           }
//           console.error("User input:", JSON.stringify(payload));
//           console.error("Error:", error);
//           console.error("Error Response:", sentryLog);
//           Sentry.captureException("Post Offcore error");
//         }
//         if (error.response != undefined) {
//           console.error(`Sorry there has been an error.
//         \nThe following exception has occurred: ${error.response.statusText}
//         \nError code: ${error.response.status}
//         \n ${error.response.data}`);
//         } else {
//           console.error(`Sorry there has been an error.
//         \nThe following exception has occurred: ${error.message}`);
//         }
//         let errorMessage = helpersFunctions.handleCallError(error);
//         console.error("message:", errorMessage);
//         return reject(errorMessage);
//       });
//   });
// }

function getPermissions(scope) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.API}/odata/Permission`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getPermissions " + (t1 - t0) + " milliseconds.");
        if (result && result.data) {
          return resolve({ data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        scope.setError(
          true,
          "Could not fetch permissions, please try again or contact your system administrator",
        );
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function postInvite(options) {
  return new Promise((resolve, reject) => {
    return client
      .post(
        `${config.API}/api/Invites?emailAddress=${options.email}&companyId=${options.companyId}&teamId=${options.teamId}`,
      )
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        console.error("user input: ", JSON.stringify(options));
        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("User input:", JSON.stringify(options));
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException("Post Invite error");
        }
        console.error("error in post call", error);
        if (error.response != undefined) {
          console.error("error in post call response", error.response);
          console.error(`Sorry there has been an error.
      \nThe following exception has occurred: ${error.response.statusText} 
      \nError code: ${error.response.status}
      \n ${error.response.data}`);
          reject(error.response);
        } else {
          console.error("error in post call message", error.message);
          console.error(`Sorry there has been an error.
      \nThe following exception has occurred: ${error.message}`);
          reject(error.message);
        }
      });
  });
}

function postPermission(options) {
  return new Promise((resolve, reject) => {
    return client
      .post(
        `${config.ADSAPI}/api/UserPermissions?userId=${options.userId}&permissionId=${options.permissionId}&companyId=${options.companyId}`,
      )
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        console.error("user input: ", JSON.stringify(options));
        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("User input:", JSON.stringify(options));
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException("Post Permission error");
        }

        if (error.response != undefined) {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
        } else {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
        }
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function deleteUserDetails(userId) {
  return new Promise((resolve, reject) => {
    return client
      .delete(
        `${config.ADSAPI}/api/UserPermissions/removeAllUserPermissionsTeamsNullUserCompanyId?userId=${userId}`,
      )
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        console.log("result", result);

        if (result) {
          return resolve({ data: result });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        console.error("user id: ", JSON.stringify(userId));
        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("User Id:", JSON.stringify(userId));
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException(error, {
            tags: {
              api: "deleteUserDetails",
              statusCode: String(error.response?.status || ""),
            },
            extra: {
              userId,
              responseSummary: sentryLog?.slice(0, 500) || "",
            },
          });
        }
        if (error.response != undefined) {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
        } else {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
        }
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function putPermissions(options) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      return client
        .put(
          `${config.ADSAPI}/api/UserPermissions/copyUser?userIdFrom=${options.userIdFrom}&userIdTo=${options.userIdTo}`,
        )
        .then((result) => {
          let stringifiedResult = JSON.stringify(result);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.info("result:", sentryLog);
          if (result && result.data) {
            return resolve({ data: result.data });
          } else {
            store.dispatch(
              alertActions.error(
                `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
              ),
            );
          }
        })
        .catch((error) => {
          let errorString = JSON.stringify(error);
          let sentryError;
          if (errorString && errorString.includes("Authorization")) {
            sentryError = errorString.split("Authorization")[0];
          } else {
            sentryError = errorString;
          }
          console.error("error:", sentryError);

          console.error("user input: ", JSON.stringify(options));
          if (
            error &&
            error.response &&
            error.response.status &&
            error.response.status != 401
          ) {
            let stringifiedResult = JSON.stringify(error.response);
            let sentryLog = stringifiedResult;
            if (
              stringifiedResult &&
              stringifiedResult.includes("Authorization")
            ) {
              let arrOne = stringifiedResult.split("Authorization");
              let arrTwo = stringifiedResult.split("transformRequest");
              sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
            }
            console.error("User input:", JSON.stringify(options));
            console.error("Error:", error);
            console.error("Error Response:", sentryLog);
            Sentry.captureException(error, {
              tags: {
                api: "putPermissions",
                statusCode: String(error.response?.status || ""),
              },
              extra: {
                userIdFrom: options?.userIdFrom,
                userIdTo: options?.userIdTo,
                responseSummary: sentryLog?.slice(0, 500) || "",
              },
            });
          }
          if (error.response != undefined) {
            console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
          } else {
            console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
          }
          let errorMessage = helpersFunctions.handleCallError(error);
          console.error("message:", errorMessage);
          return reject(errorMessage);
        });
    }, 5000);
  });
}

function putTeam(requestString) {
  return new Promise((resolve, reject) => {
    return client
      .put(requestString)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);

        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        console.error("request string: ", JSON.stringify(requestString));
        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("Request String:", JSON.stringify(requestString));
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException("Put Team error");
        }
        if (error.response != undefined) {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
        } else {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
        }
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function putUserCompany(userId, companyId) {
  return new Promise((resolve, reject) => {
    return client
      .put(
        `${config.ADSAPI}/api/Identity/addUsersCompany?userId=${userId}&companyId=${companyId}`,
      )
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException("Put user error");
        }
        if (error.response != undefined) {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
        } else {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
        }
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function postAction(options, action) {
  return new Promise((resolve, reject) => {
    return client
      .post(
        `${config.ActionAPI}/api/${options.orderType}/${options.id}/Actions/${options.actionName}`,
        action,
      )
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        console.error("payload: ", JSON.stringify(action));
        console.error("options for query: ", JSON.stringify(options));
        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("User input:", action);
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException(error, {
            tags: {
              api: "postAction",
              statusCode: String(error.response?.status || ""),
            },
            extra: {
              orderType: options?.orderType,
              orderId: options?.id,
              actionName: options?.actionName,
              responseSummary: sentryLog?.slice(0, 500) || "",
            },
          });
        }
        if (error.response != undefined) {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
        } else {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
        }
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}
function putAction(options, action) {
  return new Promise((resolve, reject) => {
    return client
      .put(
        `${config.ActionAPI}/api/${options.orderType}/${options.id}`,
        action,
      )
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        console.error("Payload: ", JSON.stringify(action));
        console.error("queryString: ", JSON.stringify(options));
        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("Payload:", action);
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException("Put Action error");
        }
        if (error.response != undefined) {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
        } else {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
        }
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function putRetry(id) {
  return new Promise((resolve, reject) => {
    return client
      .put(
        `${config.ADSAPI}/api/RepairRequests/retry?repairRequestId=${id}`,
      )
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        return resolve({ data: result });
      })
      .catch((error) => {
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException("Put retry error");
        }
        if (error.response != undefined) {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
        } else {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
        }
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function postNewRequest(queryString, action, headers = {}) {
  return new Promise((resolve, reject) => {
    return client
      .post(`${queryString}`, action, headers)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        return resolve({ data: result.data });
      })
      .catch((error) => {
        const status = error?.response?.status;
        //console output becomes Sentry breadcrumbs, so redact before logging
        console.error("queryString:", normaliseApiPath(queryString));
        console.error("user input:", redactSensitiveValues(action));
        if (shouldReportApiFailure(status)) {
          console.error(
            "error response:",
            summariseApiErrorResponse(error),
          );
          reportApiFailureToSentry({
            helper: "postNewRequest",
            method: "POST",
            url: queryString,
            status,
          });
        }
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function postNewRequestStagger(queryString, action, delay, origin) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      return client
        .post(`${queryString}`, action)
        .then((result) => {
          let stringifiedResult = JSON.stringify(result);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.info("result:", sentryLog);
          return resolve({ data: result.data });
        })
        .catch((error) => {
          //need these console.logs for sentry logging
          let errorString = JSON.stringify(error);
          let sentryError;
          if (errorString && errorString.includes("Authorization")) {
            sentryError = errorString.split("Authorization")[0];
          } else {
            sentryError = errorString;
          }
          console.error("staggererror:", sentryError);

          console.error("error:", error);

          console.error("user input: ", JSON.stringify(action));
          console.error("queryString: ", JSON.stringify(queryString));
          if (
            error &&
            error.response &&
            error.response.status &&
            error.response.status != 401
          ) {
            let stringifiedResult = JSON.stringify(error.response);
            let sentryLog = stringifiedResult;
            if (
              stringifiedResult &&
              stringifiedResult.includes("Authorization")
            ) {
              let arrOne = stringifiedResult.split("Authorization");
              let arrTwo = stringifiedResult.split("transformRequest");
              sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
            }
            console.error("User input:", action);
            console.error("Error:", error);
            console.error("Error Response:", sentryLog);
            Sentry.captureException("New request stagger error");
          }

          let errorMessage = helpersFunctions.handleCallError(error);
          console.error("message:", errorMessage);
          return reject(errorMessage);

          // if (error.response != undefined) {
          //   window.alert(`Sorry there has been an error.
          // \nThe following exception has occurred: ${error.response.statusText}
          // \nError code: ${error.response.status}
          // \n ${error.response.data}`);
          // } else {
          //   window.alert(`Sorry there has been an error.
          // \nThe following exception has occurred: ${error.message}`);
          // }
          // statusDescription: "Duplicate customer purchase order number"
          //return reject();
        });
    }, delay);
  });
}

function postVendorInvoices(queryString, action) {
  return new Promise((resolve, reject) => {
    return client
      .post(`${queryString}`, action)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        console.error("user input: ", JSON.stringify(action));
        console.error("queryString: ", JSON.stringify(queryString));
        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("User input:", JSON.stringify(action));
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException("Post Vendor Invoices error");
        }

        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getInvitees() {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.API}/odata/invites?$expand=team,company`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((response) => {
        let stringifiedResult = JSON.stringify(response);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getInvitees " + (t1 - t0) + " milliseconds.");

        if (response && response.data) {
          return resolve(response.data.value);
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => console.error(err));
}

function generateInviteCode() {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.API}/api/Invites/InviteCode`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((response) => {
        let stringifiedResult = JSON.stringify(response);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getinvitecode " + (t1 - t0) + " milliseconds.");

        if (response?.data) {
          return resolve(response.data);
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => console.error(err));
}

function getUsers(top = 75, skip = 0, searchBy = "", query = "") {
  let t0 = performance.now();
  let t1;

  //use the userId to get just 1 after making a change
  let queryString =
    searchBy == "Id"
      ? `${config.API}/odata/User?$filter=Id eq ${query} &$top=${top}&$skip=${skip}&$count=true&$expand=userpermissions($expand=permission,company),teamusers($expand=team),company&$orderby=DateCreated desc `
      : searchBy
        ? `${config.API}/odata/User?$filter=contains(toupper(${searchBy}), toupper('${query}')) eq true&$top=${top}&$skip=${skip}&$count=true&$expand=userpermissions($expand=permission,company),teamusers($expand=team),company&$orderby=DateCreated desc `
        : top == "all"
          ? `${config.API}/odata/User?$count=true&$expand=userpermissions($expand=permission,company),teamusers($expand=team),company&$orderby=DateCreated desc `
          : `${config.API}/odata/User?$top=${top}&$skip=${skip}&$count=true&$expand=userpermissions($expand=permission,company),teamusers($expand=team),company&$orderby=DateCreated desc `;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((response) => {
        let stringifiedResult = JSON.stringify(response);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getUsers " + (t1 - t0) + " milliseconds.");
        if (response && response.data) {
          let count = Object.keys(response.data).map((e) => {
            return response.data[e];
          });
          return resolve({ data: response.data.value, count: count[1] });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => console.error(err));
}

function getTeams(scope, companyId) {
  //only get teams in the new format (with transaction type)
  let t0 = performance.now();
  let t1;
  //only get the -Letter ones here (template teams)
  let companyString = companyId
    ? `Company/Id%20eq%20${companyId}%20 and `
    : ``;
  let queryString = `${config.API}/odata/companyteam?$filter=${companyString}(contains(toupper(Team/Name), toupper('-S')) eq true or contains(toupper(Team/Name), toupper('-R')) eq true or contains(toupper(Team/Name), toupper('-E')) eq true or contains(toupper(Team/Name), toupper('-R')) eq true or contains(toupper(Team/Name), toupper('-H')) eq true or contains(toupper(Team/Name), toupper('-I')) eq true)&$expand=team, company`;
  //contains(toupper(Team/Name), toupper('SalesView')) eq true or contains(toupper(Team/Name), toupper('ExchangesView')) eq true or
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((response) => {
        let stringifiedResult = JSON.stringify(response);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getTeams " + (t1 - t0) + " milliseconds.");
        if (response && response.data) {
          return resolve(response.data.value);
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        if (scope) {
          scope.setError(
            true,
            "Could not fetch teams, please try again or contact your system administrator:" +
              error,
          );
        }
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => console.error(err));
}

//get teams like "ExchangesView", that take global permissions
function getGlobalTeams(scope) {
  //only get teams in the new format (with transaction type)
  let t0 = performance.now();
  let t1;
  // filter out anything with a dash to filter out template teams
  let queryString = `${config.API}/odata/Team?$filter=contains(toupper(Name), toupper('-')) eq false`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((response) => {
        let stringifiedResult = JSON.stringify(response);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getTeams " + (t1 - t0) + " milliseconds.");
        if (response && response.data) {
          return resolve(response.data.value);
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        scope.setError(
          true,
          "Could not fetch teams, please try again or contact your system administrator",
        );
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => console.error(err));
}

function getIdentity(userId) {
  let t0 = performance.now();
  let t1;
  let queryString = `${config.ADSAPI}/api/Identity/byUserId?userId=${userId}`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((response) => {
        let stringifiedResult = JSON.stringify(response);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getIdentity " + (t1 - t0) + " milliseconds.");
        return resolve(response);
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => console.error(err));
}

function createTeam(transactionType, companyId) {
  return new Promise((resolve, reject) => {
    return client
      .post(
        `${config.ADSAPI}/api/Teams/${transactionType}TeamForCompany?companyId=${companyId}`,
      )
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        console.error("args: ", transactionType, companyId);
        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException("Create Team error");
        }
        if (error.response != undefined) {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
        } else {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
        }
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function deletePermission(userId, permissionId, companyId, scope) {
  return new Promise((resolve, reject) => {
    return client
      .delete(
        `${config.ADSAPI}/api/UserPermissions?userId=${userId}&permissionId=${permissionId}&companyId=${companyId}`,
      )
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        scope.props.setError(
          true,
          "There was a problem deleting the permission, please try again or contact your system administrator.",
          {
            sticky: true,
          },
        );
        if (error.response != undefined) {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
        } else {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
        }

        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function deleteTeamUser(teamId, userId, scope) {
  return new Promise((resolve, reject) => {
    return client
      .delete(
        `${config.ADSAPI}/api/TeamUsers?userId=${userId}&teamId=${teamId}`,
      )
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        return resolve({ data: result.data });
      })
      .catch((error) => {
        scope.props.setError(
          true,
          "There was a problem deleting the team, please try again or contact your system administrator.",
          {
            sticky: true,
          },
        );
        if (error.response != undefined) {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.response.statusText} 
        \nError code: ${error.response.status}
        \n ${error.response.data}`);
        } else {
          console.error(`Sorry there has been an error.
        \nThe following exception has occurred: ${error.message}`);
        }

        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

//Status code test
function StatusCodeTest(code) {
  let queryString = `${config.ActionAPI}/api/Helper/StatusCode?responseCode=${code}`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((response) => {
        let stringifiedResult = JSON.stringify(response);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        return resolve(response);
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => console.error(err));
}

function PowerBISettings(reportId) {
  let queryString = `${config.API}/api/Reports/PowerBiReports/$filter=report/reportId eq ${reportId}`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((response) => {
        let stringifiedResult = JSON.stringify(response);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        return resolve(response);
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => console.error(err));
}

function PowerBIGetReports(reportId) {
  console.log("reportId", reportId);
  let queryString = `${config.API}/api/Reports/PowerBiReportPermissions/${reportId}/EmbeddedUrl`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((response) => {
        let stringifiedResult = JSON.stringify(response);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        if (stringifiedResult && sentryLog.includes("embedToken")) {
          sentryLog =
            sentryLog.split("embedToken")[0] +
            sentryLog.split("reportPermissions")[
              sentryLog.split("reportPermissions").length - 1
            ];
        }
        console.info("result BI:", sentryLog);
        return resolve(response);
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => {
    console.error(err);
    throw err;
  });
}

function PowerBIGetReportsPermission() {
  let queryString = `${config.API}/api/Reports/PowerBiReportPermissions`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((response) => {
        let stringifiedResult = JSON.stringify(response);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        return resolve(response);
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => {
    console.error(err);
  });
}

function getSaleOrderDetailsInChat(so) {
  let queryString = `${config.API}/odata/order?$filter=soNumber eq '${so}' &$expand=shipment,stock`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((response) => {
        let stringifiedResult = JSON.stringify(response);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (response && response.data && response.data.value) {
          return resolve(response.data.value);
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => console.error(err));
}

function getGlobalSearchData(ref) {
  let queryString = `${config.API}/API/search/reference/${ref}`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((response) => {
        let stringifiedResult = JSON.stringify(response);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (response && response.data) {
          return resolve({ data: response.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => console.error(err));
}

//get a list of companyIds a user has a for a certain permission
//takes permission name as id can vary across systems
function checkUserPermissionCompanyIds(permissionName) {
  let queryString = `${config.ADSAPI}/api/UserCompanyPermissionStatus/forNullEquivelent?permissionName=${permissionName}`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((response) => {
        let stringifiedResult = JSON.stringify(response);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);

        return resolve(response);
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => console.error(err));
}

function getPickslips(location, status) {
  let queryString = `${config.PickslipAPI}/api/PickSlip/GetPickslipsStatus${location}/${status}`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((response) => {
        let stringifiedResult = JSON.stringify(response);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        return resolve(response);
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => console.error(err));
}

function getPricelineBatches(rowNums = 10, filtering) {
  let stem = `${config.API}/odata/PriceLineRequestBatch?$filter=`;
  let end =
    rowNums == 0
      ? `&$orderBy=Id desc`
      : `&$top=${rowNums}&$orderBy=Id desc`;
  console.log("fitlering", filtering);
  let endDate =
    filtering.queryCal &&
    filtering.searchByCal == "entryDateRange" &&
    filtering.queryCal[1]
      ? dayjs(filtering.queryCal[1]).add(1, "day").toISOString()
      : filtering.queryCal && filtering.searchByCal == ">entryDate"
        ? dayjs().toISOString()
        : filtering.searchByCal == "entryDate" && filtering.queryCal
          ? dayjs(filtering.queryCal).add(1, "day").toISOString()
          : "";

  //doing it this way to avoid extra logic to figure out if there's filtering at all and where the 'and's and spacing should be
  let archiveStr = filtering.archived
    ? `(Archived eq false or Archived eq true)`
    : `Archived eq false`;
  let userStr = filtering.userId
    ? ` and UserId eq ${filtering.userId}`
    : ``;
  let idStr = filtering.id ? ` and Id eq ${filtering.id}` : ``;

  let dateStr = filtering.queryCal
    ? ` and DateCreated ge ${
        Array.isArray(filtering.queryCal)
          ? dayjs(filtering.queryCal[0]).toISOString()
          : dayjs(filtering.queryCal).toISOString()
      } and DateCreated le ${endDate}`
    : ``;
  let queryStr = stem + archiveStr + idStr + userStr + dateStr + end;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryStr}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        let count;
        if (result && result.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getPriceLineRequests(
  rowNums = 10,
  skip = 0,
  sort = { sortby: "partNumber", sortorder: "desc" },
) {
  //removed sorting ability for price line as showing newest means using Id, which isn't displayed
  // let sortBy = sort && sort.sortby ? sort.sortby : "PartNumber";
  // let sortOrder = sort && sort.sortorder ? sort.sortorder : "desc";
  //

  let queryString =
    rowNums && rowNums > 0
      ? `${config.API}/odata/PriceLineRequests?$filter=archived eq false&$top=${rowNums}&$skip=${skip}&$orderby=Id desc&$count=true`
      : `${config.API}/odata/PriceLineRequests?$filter=archived eq false&$orderby=Id desc&$count=true`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        let count;
        if (result?.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          result.data.value.map((item) => {
            // EntryDate
            item.EntryDate = dayjs(item.EntryDate)
              .format("DD-MMM-YYYY")
              .toString()
              .toUpperCase();
          });
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => console.error(err));
}

function getPriceLineRequestsQuery(
  options,
  sort = { sortby: "partNumber", sortorder: "desc" },
) {
  //debugger;
  const numberSearches = ["LeadTime", "Qty", "MinorPrice", "MajorPrice"];
  let rows =
    options && options.rows == "none"
      ? 0
      : options.rows
        ? options.rows
        : options.rows === 0
          ? "all"
          : 10;

  let rowsString = rows !== "all" ? `&$top=${rows}` : "";
  let skip = options && options.skip ? options.skip : 0;

  let queryIsNum =
    numberSearches.includes(options.property) && options.query;

  let searchBy = options.property && options.property;

  //if we enter a date range, we need to format it correctly before sending. It comes in as an array so 0th is start end 1st is end of date range
  let startDate =
    options.property && options.property == "entryDateRange"
      ? dayjs(options.query[0]).toISOString()
      : undefined;

  // If we are looking at one specific day, it looks between chosen day and day after at midnight

  let endDate =
    options.priceLineFiltering.queryCal &&
    options.priceLineFiltering.searchByCal == "entryDateRange"
      ? dayjs(options.priceLineFiltering.queryCal[1])
          .add(1, "day")
          .toISOString()
      : options.priceLineFiltering.queryCal &&
          options.priceLineFiltering.searchByCal == ">entryDate"
        ? dayjs().toISOString()
        : options.priceLineFiltering.searchByCal == "entryDate" &&
            options.priceLineFiltering.queryCal
          ? dayjs(options.priceLineFiltering.queryCal)
              .add(1, "day")
              .toISOString()
          : "";

  let stem = `${config.API}/odata/PriceLineRequests?$filter=`;
  //as this is a filter query, should always need a filter

  //might still get a date as the main search as we have effective from and effective to, or a number, or a string
  let searchInputString = queryIsNum
    ? ` and ${searchBy} eq ${options.query}`
    : ` and contains(${searchBy},'${options.query}')`;

  let validatedString =
    options.priceLineFiltering.validated ||
    options.priceLineFiltering.validated === false
      ? ` and validated eq ${options.priceLineFiltering.validated}`
      : ``;
  let signedOffString =
    options.priceLineFiltering.signedOff ||
    options.priceLineFiltering.signedOff === false
      ? ` and signoffStatus eq ${options.priceLineFiltering.signedOff}`
      : ``;
  let dateString = options.priceLineFiltering.queryCal
    ? ` and DateCreated ge ${
        startDate
          ? startDate
          : Array.isArray(options.priceLineFiltering.queryCal)
            ? dayjs(options.priceLineFiltering.queryCal[0]).toISOString()
            : dayjs(options.priceLineFiltering.queryCal).toISOString()
      } and DateCreated le ${endDate}`
    : ``;

  let batchString = options.priceLineFiltering.batch
    ? ` and PriceLineRequestBatchId eq ${options.priceLineFiltering.batch}`
    : ``;

  let userId = options.priceLineFiltering.userId
    ? ` and userId eq ${options.priceLineFiltering.userId}`
    : ``;

  //put this first to avoid logic around where to put 'and's and spaces
  let archived = options.priceLineFiltering.archived
    ? `(archived eq true or archived eq false)`
    : `archived eq ${options.priceLineFiltering.archived}`;

  let end =
    rows == 0
      ? ` ${rowsString}&$orderby=Id desc&$count=true`
      : ` ${rowsString}&$skip=${skip}&$orderby=Id desc&$count=true`;

  let queryString =
    stem +
    archived +
    searchInputString +
    validatedString +
    signedOffString +
    dateString +
    batchString +
    userId +
    end;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        let count;
        if (result && result.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => console.error(err));
}

//get all price line items that can be signed off- already validated but not yet signoffstatus
function getPriceLineAllSignOffable() {
  let queryString = `${config.API}/odata/PriceLineRequests?$filter=Archived eq false and Validated eq true and SignOffStatus eq false`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve(result.data.value);
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  }).catch((err) => console.error(err));
}

function putPriceLine(query, payload) {
  return new Promise((resolve, reject) => {
    return client
      .put(query, payload)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        //bug fix for donald
        return resolve({ data: result });
      })
      .catch((error) => {
        let errorString = JSON.stringify(error);
        let sentryError;
        if (errorString && errorString.includes("Authorization")) {
          sentryError = errorString.split("Authorization")[0];
        } else {
          sentryError = errorString;
        }
        console.error("error:", sentryError);

        console.error("payload: ", JSON.stringify(payload));
        console.error("queryString: ", JSON.stringify(query));
        if (
          error &&
          error.response &&
          error.response.status &&
          error.response.status != 401
        ) {
          let stringifiedResult = JSON.stringify(error.response);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            let arrOne = stringifiedResult.split("Authorization");
            let arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.error("Payload:", JSON.stringify(payload));
          console.error("Error:", error);
          console.error("Error Response:", sentryLog);
          Sentry.captureException("Put priceline error");
        }
        console.log("ERROR HERE", error, error.message, error.response);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getWizardBatch(
  rows = 30,
  skip = 0,
  batchCode,
  searchBy,
  queryStr,
) {
  console.log("batrchVocede", batchCode);
  let numSearches = ["BatchUpdateRequestType", "Id"];
  let queryString =
    (queryStr || queryStr === 0) && numSearches.includes(searchBy)
      ? `${config.API}/odata/BatchUpdateRequest?$filter=BatchUpdateRequestType eq ${batchCode} and ${searchBy} eq ${queryStr} &$orderby=Id desc&$top=${rows}&$skip=${skip}&$count=true`
      : queryStr
        ? `${config.API}/odata/BatchUpdateRequest?$filter=contains(toupper(${searchBy}), toupper('${queryStr}')) eq true and BatchUpdateRequestType eq ${batchCode}&$orderby=Id desc&$top=${rows}&$skip=${skip}&$count=true`
        : `${config.API}/odata/BatchUpdateRequest?$filter=BatchUpdateRequestType eq ${batchCode} &$orderby=Id desc&$top=${rows}&$skip=${skip}&$count=true`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        let count;
        if (result && result.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getWizardBatchResultFilt(
  rows = 30,
  skip = 0,
  batchCode,
  searchBy,
  queryStr,
) {
  let queryString = !queryStr
    ? `${config.API}/odata/BatchUpdateRequest?$filter=BatchUpdateRequestType eq ${batchCode} &$orderby=Id desc&$top=${rows}&$skip=${skip}&$count=true`
    : queryStr && queryStr == "Success"
      ? `${config.API}/odata/BatchUpdateRequest?$filter=BatchUpdateRequestType eq ${batchCode} and Completed eq true and ReportLogS3FileName eq null&$orderby=Id desc&$top=${rows}&$skip=${skip}&$count=true`
      : queryStr == "Failure"
        ? `${config.API}/odata/BatchUpdateRequest?$filter=BatchUpdateRequestType eq ${batchCode} and Completed eq true and ReportLogS3FileName ne null&$orderby=Id desc&$top=${rows}&$skip=${skip}&$count=true`
        : //not completed
          `${config.API}/odata/BatchUpdateRequest?$filter=BatchUpdateRequestType eq ${batchCode} and Completed eq false&$orderby=Id desc&$top=${rows}&$skip=${skip}&$count=true`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        let count;
        if (result && result.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getReturnAirwayBill(rows = 30, skip = 0, searchBy, queryStr) {
  let queryString = queryStr
    ? `${config.API}/odata/ReturnAirwaybillRequest?$filter=contains(toupper(${searchBy}), toupper('${queryStr}')) eq true&$orderby=id desc&$top=${rows}&$skip=${skip}&$count=true`
    : `${config.API}/odata/ReturnAirwaybillRequest?$orderby=id desc&$top=${rows}&$skip=${skip}&$count=true`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        let count;
        if (result?.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getPartCreatorParts(rows = 30, skip = 0, searchBy, queryStr) {
  let queryString = queryStr
    ? `${config.API}/odata/CreatePartRequests?$filter=contains(toupper(${searchBy}), toupper('${queryStr}')) eq true&$orderby=id desc&$top=${rows}&$skip=${skip}&$count=true`
    : `${config.API}/odata/CreatePartRequests?$orderby=id desc&$top=${rows}&$skip=${skip}&$count=true`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        let count;
        if (result?.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}
function getPartCreatorPartsNoQuery(rows = 30, skip = 0) {
  let queryString = `${config.API}/odata/CreatePartRequests?$orderby=id desc&$top=${rows}&$skip=${skip}&$count=true`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        let count;
        if (result?.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getPartCreatorPartsQuery(options, sort) {
  //debugger;
  let searchBy = options.property;
  let queryStr = options.query;
  let rows = options.rows;
  let skip = options.skip;
  let queryString = queryStr
    ? `${config.API}/odata/CreatePartRequests?$filter=contains(toupper(${searchBy}), toupper('${queryStr}')) eq true&$orderby=id desc&$top=${rows}&$skip=${skip}&$count=true`
    : `${config.API}/odata/CreatePartRequests?$orderby=id desc&$top=${rows}&$skip=${skip}&$count=true`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        let count;
        if (result?.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getFlightHours(rows = 30, skip = 0, searchBy, queryStr) {
  let queryString = queryStr
    ? `${config.API}/odata/AircraftFlightLogRequests?$filter=contains(toupper(${searchBy}), toupper('${queryStr}')) eq true&$orderby=id desc&$top=${rows}&$skip=${skip}&$count=true`
    : `${config.API}/odata/AircraftFlightLogRequests?$orderby=id desc&$top=${rows}&$skip=${skip}&$count=true`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        let count;
        if (result?.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getRecentActions() {
  let queryString = `${config.ActionAPI}/api/Helper/RecentActions`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);

        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getWizardLink(id) {
  let queryString = `${config.ADSAPI}/api/BatchUpdateRequests/${id}/File`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}
function getWizardResult(id) {
  let queryString = `${config.ADSAPI}/api/BatchUpdateRequests/${id}/Log`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}
function getWizardBatchTypes() {
  let queryString = `${config.ADSAPI}/api/BatchUpdateRequestTypes`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getTableFields() {
  let queryString = `${config.ADSAPI}/api/CompanyContactsFieldsOnly`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getCompanyContacts(rows = 30, skip = 0, searchBy, queryStr) {
  let queryString = queryStr
    ? `${config.API}/odata/CompanyContacts?$filter=${searchBy} eq ${queryStr}&$top=${rows}&$skip=${skip}&$count=true&$orderby=Id desc`
    : `${config.API}/odata/CompanyContacts?$top=${rows}&$skip=${skip}&$count=true&$orderby=Id desc`;

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        let count;
        if (result && result.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        if (result && result.data) {
          return resolve({ count: count[1], data: result.data.value });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getCompanyContactIds() {
  let queryString = `${config.ADSAPI}/api/CompanyContacts/justCompanyIds`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getAllPermissions() {
  let queryString = `${config.ADSAPI}/api/Permissions`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}
function getCompanyContactByCompany(companyId) {
  let queryString = `${config.ADSAPI}/api/CompanyContacts/byCompanyId?companyId=${companyId}`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getAircraftByCompany(companyId) {
  let queryString = `${config.ADSAPI}/api/Aircrafts/byCompanyIdViaQuantum/${companyId}`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}

function getKPIData(field) {
  let queryString = `${config.ActionAPI}/api/KPI/KPI${field}`;
  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then((result) => {
        let stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          let arrOne = stringifiedResult.split("Authorization");
          let arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result.data });
        } else {
          store.dispatch(
            alertActions.error(
              `There was a problem getting the data, please try again. If the problem persists after logging out and back in again, please contact your system administrator.`,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("error: ", error);
        let errorMessage = helpersFunctions.handleCallError(error);
        console.error("message:", errorMessage);
        return reject(errorMessage);
      });
  });
}
